import moment from "moment"

import PresentationReducerBuilder from "./build-presentation-reducer"
import {
    NOTIFICATION_ITEM_REQUEST as ITEM_REQUEST,
    NOTIFICATION_ITEM_RESPONSE as ITEM_RESPONSE,
    NOTIFICATION_ITEM_SAVE_RESPONSE as ITEM_SAVE_RESPONSE,
    NOTIFICATION_ITEM_CACHED_RESPONSE as ITEM_CACHED_RESPONSE,
    NOTIFICATION_ITEM_DELETE_REQUEST as ITEM_DELETE_REQUEST,
    NOTIFICATION_ITEM_DELETE_RESPONSE as ITEM_DELETE_RESPONSE,
    NOTIFICATION_ITEM_DELETE_ERROR_RESPONSE as ITEM_DELETE_ERROR_RESPONSE,
    NOTIFICATION_LIST_REQUEST as LIST_PAGE_REQUEST,
    NOTIFICATION_LIST_RESPONSE as LIST_RESPONSE,
    NOTIFICATION_LIST_CACHED_RESPONSE as LIST_CACHED_RESPONSE,
    NOTIFICATION_SET_FILTER as SET_FILTER,
    NOTIFICATION_SET_EDIT_MODE as SET_EDIT_MODE,
    NOTIFICATION_UPDATE_FILTER_CRITERIA as UPDATE_FILTER_CRITERIA,
    NOTIFICATION_CLEAR_FILTER_CRITERIA as CLEAR_FILTER_CRITERIA,
    NOTIFICATION_UPDATE_MODEL as UPDATE_MODEL,
    NOTIFICATION_APPLY_MODEL_UPDATE as APPLY_MODEL_UPDATE,
    NOTIFICATION_CANCEL_MODEL_UPDATE as CANCEL_MODEL_UPDATE,
    NOTIFICATION_ADD_NEW as ADD_NEW,
    NOTIFICATION_TOGGLE_EXPIRES,
} from "../../actions/action-types"
import {
    toUI as notificationToUI,
    updateNotificationChangeModel,
    NotificationModel,
    NotificationModelRecord,
    NotificationCreateModel,
    updateNotificationCreateModel,
} from "../../models/entities/notification"
import {
    SourceFilterModel,
    updateSourceSearchFilterModel,
} from "../../models/filters/source-filter"

import { defaultSearchCriteria } from "../paging/source-paging-reducer"
import { serialiseSearchCriteria } from "../paging/paging-reducer-utils"
import { getNotificationFutureDate } from "../../components/notifications/util"

const reducerBuilder = new PresentationReducerBuilder()
const defaultReducer = reducerBuilder
    .actionsToHandle({
        UPDATE_MODEL,
        APPLY_MODEL_UPDATE,
        CANCEL_MODEL_UPDATE,
        CLEAR_FILTER_CRITERIA,
        UPDATE_FILTER_CRITERIA,
        SET_FILTER,
        SET_EDIT_MODE,
        ITEM_REQUEST,
        ITEM_RESPONSE,
        ITEM_SAVE_RESPONSE,
        ITEM_CACHED_RESPONSE,
        ITEM_DELETE_REQUEST,
        ITEM_DELETE_RESPONSE,
        ITEM_DELETE_ERROR_RESPONSE,
        LIST_PAGE_REQUEST,
        LIST_RESPONSE,
        LIST_CACHED_RESPONSE,
        ADD_NEW,
    })
    .updateChangeModel(updateNotificationChangeModel)
    .updateCreateModel(updateNotificationCreateModel)
    .workingModelToUI(notificationToUI)
    .workingModelClass(NotificationModelRecord)
    .workingFilterModelClass(SourceFilterModel)
    .updateSearchFilterModel(updateSourceSearchFilterModel)
    .canCancel(() => true)
    .createModelClass(NotificationCreateModel)
    .canSave(
        (updateModel, _, workingModel) =>
            workingModel.title &&
            (workingModel.link || workingModel.entity_id) &&
            workingModel.type &&
            (!workingModel.expires ||
                !moment(workingModel.not_before)
                    .clone()
                    .add(1, "hours")
                    .isAfter(moment(workingModel.expires), "minute")) &&
            updateModel.size > 0
    )
    .defaultState({
        workingModel: NotificationModel(),
        searchCriteria: serialiseSearchCriteria(defaultSearchCriteria),
        searchFilterModel: defaultSearchCriteria,
        workingFilterModel: new SourceFilterModel(),
    })
    .build()

export default (state = undefined, action) => {
    const defaultReduction = defaultReducer(state, action)
    const updateFunction = reducerBuilder.getUpdateModelFunction(
        defaultReduction.isCreateMode
    )
    switch (action.type) {
        case UPDATE_MODEL: {
            let { workingModel, updateModel } = defaultReduction
            const { name } = action

            if (["not_before", "expires"].includes(name)) {
                const currentDate = getNotificationFutureDate()

                // If through a loophole "not_before" ends up in the past, set it
                // to the current date
                if (
                    name === "not_before" &&
                    currentDate.isAfter(workingModel.not_before)
                ) {
                    const newValue = currentDate.clone()
                    updateModel = updateFunction(
                        name,
                        newValue,
                        updateModel,
                        workingModel
                    )
                    workingModel = workingModel.set(name, newValue)
                }

                // If through a loophole ("not_before" or current time) ends up
                // ahead of or within 1hr of "expires", then set it to the same
                let referenceDate = currentDate.isAfter(workingModel.not_before)
                    ? currentDate
                    : workingModel.not_before
                referenceDate = referenceDate.clone().add(1, "hours")
                if (
                    workingModel.expires &&
                    referenceDate.isAfter(workingModel.expires)
                ) {
                    updateModel = updateFunction(
                        "expires",
                        referenceDate,
                        updateModel,
                        workingModel
                    )
                    workingModel = workingModel.set("expires", referenceDate)
                }

                return {
                    ...defaultReduction,
                    workingModel,
                    updateModel,
                    canCancel: reducerBuilder._canCancel(
                        updateModel,
                        state.imageToUpload,
                        workingModel
                    ),
                    canSave: reducerBuilder._canSave(
                        updateModel,
                        state.imageToUpload,
                        workingModel
                    ),
                }
            } else if (["link", "entity_id"].includes(name)) {
                const oppositeField = name === "link" ? "entity_id" : "link"
                workingModel = workingModel.set(oppositeField, "")
                updateModel = updateModel.remove(oppositeField)

                return {
                    ...defaultReduction,
                    workingModel,
                    updateModel,
                    canCancel: reducerBuilder._canCancel(
                        updateModel,
                        state.imageToUpload,
                        workingModel
                    ),
                    canSave: reducerBuilder._canSave(
                        updateModel,
                        state.imageToUpload,
                        workingModel
                    ),
                }
            }

            return defaultReduction
        }
        case NOTIFICATION_TOGGLE_EXPIRES: {
            let { workingModel, updateModel } = defaultReduction
            let newExpires = null
            if (!workingModel.expires) {
                const currentTime = getNotificationFutureDate()
                const notBefore = moment(workingModel.not_before).clone()
                newExpires = (notBefore.isAfter(currentTime)
                    ? notBefore
                    : currentTime
                ).add(1, "hours")
            }
            workingModel = workingModel.set("expires", newExpires)
            if (newExpires) {
                updateModel = updateFunction(
                    "expires",
                    newExpires,
                    updateModel,
                    workingModel
                )
            } else {
                updateModel = updateModel.set("expires", newExpires)
            }
            return {
                ...defaultReduction,
                workingModel,
                updateModel,
                canCancel: reducerBuilder._canCancel(
                    updateModel,
                    state.imageToUpload,
                    workingModel
                ),
                canSave: reducerBuilder._canSave(
                    updateModel,
                    state.imageToUpload,
                    workingModel
                ),
            }
        }
        default:
            return defaultReduction
    }
}
